import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { scheduleService } from '@/services/scheduleService'
import type { CreateScheduleInput, UpdateScheduleInput } from '@/types/schedule'

export const scheduleKeys = {
  all: ['schedules'] as const,
  byBatch: (batchId: string) => [...scheduleKeys.all, 'batch', batchId] as const,
  detail: (id: string) => [...scheduleKeys.all, 'detail', id] as const,
  upcoming: (instructorId: string) => [...scheduleKeys.all, 'upcoming', instructorId] as const,
}

export const useSchedulesByBatch = (batchId: string) => {
  return useQuery({
    queryKey: scheduleKeys.byBatch(batchId),
    queryFn: () => scheduleService.getSchedulesByBatch(batchId),
    enabled: !!batchId,
  })
}

export const useCreateSchedule = (batchId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateScheduleInput) => scheduleService.createSchedule(batchId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.byBatch(batchId) })
    },
  })
}

export const useUpdateSchedule = (batchId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateScheduleInput }) =>
      scheduleService.updateSchedule(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.byBatch(batchId) })
      queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(variables.id) })
    },
  })
}

export const useDeleteSchedule = (batchId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => scheduleService.deleteSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.byBatch(batchId) })
    },
  })
}
